import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcCafe } from './CalccafeContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './ReceitaCafe.css';

// Funções de formatação
const formatCurrencyForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
};

const ReceitaCafe = () => {
  const { dados, setDados } = useCalcCafe();
  const navigate = useNavigate();

  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [mostrarAjuda, setMostrarAjuda] = useState(false);
  const [mensagemErro, setMensagemErro] = useState("");

  useEffect(() => {
    if (!dados.receita) {
      setDados(prev => ({
        ...prev,
        receita: { precoSaca: '' }
      }));
    }
    if (dados.resultadosReceita) {
      setMostrarResultados(true);
    }
  }, [dados.receita, dados.resultadosReceita, setDados]);

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({ ...prev, receita: { ...prev.receita, [name]: rawValue } }));
  };

  const handleCalcular = () => {
    // 1. Puxa dados das telas anteriores
    const producaoTotalSacas = (Number(dados.producao?.producaoTotal) || 0) / 100;
    const custoOperacionalTotalCafe = parseFloat(dados.resultadosCustos?.custoOperacionalCafe) || 0;
    const custoTotalCafe = parseFloat(dados.resultadosCustos?.custoTotalCafe) || 0;
    const custoOperacionalPorSaca = parseFloat(dados.resultadosCustos?.custoOperacionalSaca) || 0;
    const depreciacaoCafe = parseFloat(dados.resultadosCapital?.depreciacao) || 0;
    const rentabilidadeCapitalInvestidoCafe = parseFloat(dados.resultadosCapital?.rentabilidadeEsperadaCapital) || 0;
    
    // Validação de dados anteriores
    let erroMsg = "";
    if (producaoTotalSacas <= 0 || !custoOperacionalTotalCafe || !custoTotalCafe || !rentabilidadeCapitalInvestidoCafe) {
        erroMsg = "Dados das telas de Produção, Capital e Custos são necessários. Por favor, calcule-os primeiro.";
        setMensagemErro(erroMsg);
        setMostrarResultados(false);
        return;
    } else {
        setMensagemErro("");
    }
    
    // 2. Puxa dados desta tela
    const precoSaca = (Number(dados.receita?.precoSaca) || 0) / 100;
    if (precoSaca <= 0) {
        alert("Por favor, insira um preço de saca válido.");
        return;
    }

    // 3. Executa os cálculos
    const receitaTotalCafe = precoSaca * producaoTotalSacas;
    const lucroOperacionalCafe = receitaTotalCafe - custoOperacionalTotalCafe;
    const lucroOperacionalPorSaca = producaoTotalSacas > 0 ? lucroOperacionalCafe / producaoTotalSacas : 0;
    const lucroTotalCafe = receitaTotalCafe - custoTotalCafe;
    const lucroTotalPorSaca = producaoTotalSacas > 0 ? lucroTotalCafe / producaoTotalSacas : 0;
    const pontoEquilibrioSacas = (precoSaca - custoOperacionalPorSaca) > 0
        ? (rentabilidadeCapitalInvestidoCafe + depreciacaoCafe) / (precoSaca - custoOperacionalPorSaca)
        : 0;
    const rentabilidadeCulturaCapitalInvestido = rentabilidadeCapitalInvestidoCafe > 0
        ? (lucroTotalCafe / rentabilidadeCapitalInvestidoCafe) * 100
        : 0;

    const resultados = {
        receitaTotalCafe: receitaTotalCafe.toFixed(2), lucroOperacionalCafe: lucroOperacionalCafe.toFixed(2),
        lucroOperacionalPorSaca: lucroOperacionalPorSaca.toFixed(2), lucroTotalCafe: lucroTotalCafe.toFixed(2),
        lucroTotalPorSaca: lucroTotalPorSaca.toFixed(2), pontoEquilibrioSacas: pontoEquilibrioSacas.toFixed(2),
        rentabilidadeCulturaCapitalInvestido: rentabilidadeCulturaCapitalInvestido.toFixed(2),
    };

    setDados(prev => ({ ...prev, resultadosReceita: resultados }));
    setMostrarResultados(true);
  };

  const formatCurrencyResult = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatNumberResult = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  const handleDownloadPDF = () => { /* Adicione sua lógica de PDF aqui */ };
  const handleLimparDados = () => {
    if(window.confirm("Tem certeza que deseja limpar os dados desta tela?")) {
        setDados(prev => {
            const { receita, resultadosReceita, ...resto } = prev;
            localStorage.setItem('calcCafeData', JSON.stringify(resto));
            return resto;
        });
        setMostrarResultados(false);
    }
  };

  return (
    <div className="cafe-receita-container">
      <div className="cafe-receita-card">
        <h1>Receita e Rentabilidade</h1>
        <p className="cafe-receita-subtitle">Informe o preço de venda para calcular a rentabilidade da sua lavoura.</p>

        <div className="cafe-receita-form-results-grid">
            <div className="cafe-receita-form">
                <div className="cafe-receita-input-group">
                    <label>Preço da Saca (60kg) (R$)</label>
                    <input type="text" name="precoSaca" placeholder="Ex: 850,00" value={formatCurrencyForDisplay(dados.receita?.precoSaca)} onChange={handleCurrencyChange} />
                </div>
                <button className="cafe-receita-button" onClick={handleCalcular}>Calcular Rentabilidade</button>
                <button className="cafe-receita-help-button" onClick={() => setMostrarAjuda(true)}>Precisa de ajuda?</button>
            </div>

            {mostrarResultados && (
              <div className="cafe-receita-results">
                <h2>Resultados</h2>
                <ul>
                  <li>Receita Total: <strong>{formatCurrencyResult(dados.resultadosReceita?.receitaTotalCafe)}</strong></li>
                  <li>Lucro Operacional: <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroOperacionalCafe)}</strong></li>
                  <li>Lucro Operacional por Saca: <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroOperacionalPorSaca)}</strong></li>
                  <li>Lucro Total (Líquido): <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroTotalCafe)}</strong></li>
                  <li>Lucro Total por Saca: <strong>{formatCurrencyResult(dados.resultadosReceita?.lucroTotalPorSaca)}</strong></li>
                  <li>Ponto de Equilíbrio: <strong>{formatNumberResult(dados.resultadosReceita?.pontoEquilibrioSacas)} Sacas</strong></li>
                  <li>Rentabilidade (Cultura vs Capital): <strong>{formatNumberResult(dados.resultadosReceita?.rentabilidadeCulturaCapitalInvestido)} %</strong></li>
                </ul>
                <button className="cafe-receita-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
              </div>
            )}
        </div>

        {mensagemErro && <p className="cafe-receita-warning">{mensagemErro}</p>}
        
        <div className="cafe-receita-navigation">
            <button className="cafe-receita-button back" onClick={() => navigate('/calccafe/app/custosCafe')}>Voltar para Custos</button>
            <button className="cafe-receita-button clean" onClick={handleLimparDados}>Limpar Dados</button>
            <button className="cafe-receita-button navigate" onClick={() => navigate('/calccafe/app/relatorioCafe')}>Avançar para Relatório Final</button>
        </div>
      </div>
      
      {mostrarAjuda && (
        <div className="ajuda-modal-backdrop" onClick={() => setMostrarAjuda(false)}>
            <div className="ajuda-modal-content" onClick={e => e.stopPropagation()}>
                <h2>Ajuda - Receita e Rentabilidade</h2>
                <p><strong>Preço da Saca:</strong> Informe o preço médio de venda da saca de 60kg do seu café.</p>
                <p><strong>Ponto de Equilíbrio:</strong> Indica quantas sacas você precisa vender apenas para cobrir todos os seus custos (operacionais + capital). Vendas acima deste valor representam seu lucro líquido.</p>
                <p><strong>Rentabilidade:</strong> Compara o lucro líquido do seu negócio com o que você ganharia se tivesse investido todo o capital no mercado financeiro (com a taxa de remuneração que você definiu na tela de Capital).</p>
                <button onClick={() => setMostrarAjuda(false)}>Fechar</button>
            </div>
        </div>
      )}
    </div>
  );
};

export default ReceitaCafe;
